import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Star } from "lucide-react";
import Seo, { breadcrumbSchema, faqSchema, serviceSchema } from "../components/Seo";
import { Reveal, Overline, FrameReveal } from "../components/Reveal";
import CtaBand from "../components/CtaBand";
import RelatedInsights from "../components/RelatedInsights";
import { useService, useServices, useProjects, useSettings, useAreas } from "../lib/hooks";

function ListSection({ num, title, items, dark = false }) {
  if (!items?.length) return null;
  return (
    <Reveal>
      <div className={`grid lg:grid-cols-12 gap-8 py-14 md:py-20 border-t ${dark ? "border-white/10" : "border-black/10"}`}>
        <div className="lg:col-span-4">
          <p className="font-mono text-xs text-ember">[ {num} ]</p>
          <h2 className="font-display font-light text-3xl md:text-4xl tracking-tight mt-4">{title}</h2>
        </div>
        <ul className="lg:col-span-8 grid sm:grid-cols-2 gap-x-10 gap-y-5">
          {items.map((it, i) => (
            <li key={i} className={`flex gap-4 text-sm md:text-base leading-relaxed ${dark ? "text-white/70" : "text-graphite/70"}`}>
              <span className="font-mono text-[10px] text-ember mt-1.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
              {it}
            </li>
          ))}
        </ul>
      </div>
    </Reveal>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const { data: service, isLoading, isError } = useService(slug);
  const { data: allServices } = useServices();
  const { data: projects } = useProjects();
  const { data: settings } = useSettings();
  const { data: allAreas } = useAreas();

  if (isLoading) return <div className="min-h-screen bg-graphite" />;
  if (isError || !service) {
    return (
      <div className="min-h-screen bg-graphite text-white flex items-center justify-center">
        <p data-testid="service-not-found">Service not found. <Link to="/services" className="text-ember underline">View all services</Link>.</p>
      </div>
    );
  }

  const related = (projects || []).filter((p) => (p.related_services || []).includes(slug)).slice(0, 3);
  const HUB_ORDER = ["builders-bristol", "builders-bath", "builders-somerset", "builders-south-wales", "builders-devon", "builders-weston-super-mare"];
  const serviceSpecific = (allAreas || []).filter((a) => a.slug.startsWith(slug.replace(/s$/, "")) || a.slug.startsWith(slug));
  const hubs = HUB_ORDER.map((h) => (allAreas || []).find((a) => a.slug === h)).filter(Boolean);
  const serviceAreas = [...serviceSpecific, ...hubs.filter((h) => !serviceSpecific.includes(h))].slice(0, 8);

  return (
    <div data-testid="service-detail-page">
      <Seo
        title={service.seo_title || `${service.title} | NOOR Design & Build`}
        description={service.seo_description || service.summary}
        path={`/services/${slug}`}
        image={service.image}
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            serviceSchema(service, settings),
            breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: service.title, path: `/services/${slug}` }]),
            ...(service.faqs?.length ? [faqSchema(service.faqs)] : []),
          ],
        }}
      />

      <section className="bg-graphite text-white relative overflow-hidden">
        <div className="absolute inset-0 blueprint-grid" aria-hidden="true" />
        <div className="relative max-w-[1440px] mx-auto px-5 md:px-10 pt-32 md:pt-44 pb-16 md:pb-24 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <nav className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40" aria-label="Breadcrumb" data-testid="service-breadcrumb">
              <Link to="/" className="hover:text-ember">Home</Link> / <Link to="/services" className="hover:text-ember">Services</Link> / <span className="text-ember">{service.title}</span>
            </nav>
            <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-6 leading-[1.05]">
              {service.h1 || service.title}
            </h1>
            <p className="text-white/60 text-base md:text-lg leading-relaxed mt-8 max-w-xl">{service.intro}</p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 mt-10">
              <Link
                to="/request-a-consultation"
                data-testid="service-consultation-button"
                className="btn-sheen group inline-flex items-center justify-center gap-3 bg-ember hover:bg-emberdark text-white font-semibold tracking-wide px-8 py-4 text-sm transition-colors duration-300"
              >
                Request a Consultation <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/how-we-work"
                data-testid="service-howwework-link"
                className="inline-flex items-center justify-center gap-3 border border-white/20 hover:border-ember text-white/80 hover:text-white px-8 py-4 text-sm font-semibold tracking-wide transition-colors duration-300"
              >
                How We Work
              </Link>
            </div>
            {service.area_link && (
              <Link
                to={service.area_link.to}
                data-testid="service-area-link"
                className="group inline-flex items-center gap-2 mt-6 text-sm font-semibold text-white/70 hover:text-ember transition-colors duration-300"
              >
                {service.area_link.label} <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            )}
          </div>
          {service.image && (
            <div className="lg:col-span-5 border border-white/15 p-2">
              <FrameReveal src={service.image} alt={`${service.title} — NOOR Design & Build`} className="aspect-[4/3]" hoverZoom={false} eager />
            </div>
          )}
        </div>
      </section>

      <section className="bg-cream">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 pb-16 md:pb-24">
          <ListSection num="01" title="Common client objectives" items={service.objectives} />
          <ListSection num="02" title="What NOOR can manage" items={service.manages} />
          <ListSection num="03" title="Typical project stages" items={service.stages} />
          <Reveal>
            <div className="grid lg:grid-cols-2 gap-px bg-black/10 border border-black/10 mt-4">
              <div className="bg-cream p-8 md:p-12">
                <p className="font-mono text-xs text-ember">[ 04 ]</p>
                <h2 className="font-display font-light text-2xl md:text-3xl tracking-tight mt-4">Design &amp; structural considerations</h2>
                <p className="text-graphite/65 leading-relaxed mt-5 text-sm md:text-base">{service.design_notes}</p>
              </div>
              <div className="bg-cream p-8 md:p-12">
                <p className="font-mono text-xs text-ember">[ 05 ]</p>
                <h2 className="font-display font-light text-2xl md:text-3xl tracking-tight mt-4">Planning &amp; Building Regulations</h2>
                <p className="text-graphite/65 leading-relaxed mt-5 text-sm md:text-base">{service.regs_notes}</p>
              </div>
            </div>
          </Reveal>

          {service.sections?.length > 0 && (
            <div className="mt-14 space-y-12 max-w-[920px]" data-testid="service-sections">
              {service.sections.map((s, i) => (
                <Reveal key={i}>
                  <h2 className="font-display font-light text-3xl md:text-4xl tracking-tight" data-testid={`service-section-h2-${i}`}>{s.h2}</h2>
                  {(Array.isArray(s.text) ? s.text : [s.text]).map((p, j) => (
                    <p key={j} className="text-graphite/70 leading-relaxed mt-5 text-sm md:text-base">{p}</p>
                  ))}
                  {s.links?.length > 0 && (
                    <div className="flex flex-wrap gap-x-8 gap-y-3 mt-6">
                      {s.links.map((l) => (
                        <Link key={l.to} to={l.to} className="group inline-flex items-center gap-2 text-sm font-semibold text-graphite hover:text-ember transition-colors duration-300">
                          {l.label} <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                      ))}
                    </div>
                  )}
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {service.review && (
        <section className="bg-stone">
          <div className="max-w-[900px] mx-auto px-5 md:px-10 py-16 md:py-24">
            <Reveal>
              <figure data-testid="service-review">
                <div className="flex items-center gap-1" aria-label="Five star Google review">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} size={14} className="text-[#D4A017]" fill="#D4A017" strokeWidth={0} />
                  ))}
                </div>
                <blockquote className="serif-accent text-graphite/80 leading-relaxed text-lg md:text-xl mt-5">“{service.review.text}”</blockquote>
                <figcaption className="font-mono text-[10px] tracking-[0.25em] uppercase text-graphite/40 mt-5">
                  {service.review.name}{service.review.location ? ` — ${service.review.location}` : ""} · Google review
                </figcaption>
              </figure>
            </Reveal>
          </div>
        </section>
      )}

      {related.length > 0 && (
        <section className="bg-stone">
          <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-24">
            <Reveal>
              <Overline>[ Related Projects ]</Overline>
            </Reveal>
            <div className="grid md:grid-cols-3 gap-6 mt-10">
              {related.map((p) => (
                <Reveal key={p.slug}>
                  <Link to={`/projects/${p.slug}`} data-testid={`related-project-${p.slug}`} className="group block">
                    <div className="border border-black/10 p-2 bg-cream">
                      <FrameReveal src={p.cover} alt={`${p.title} — ${p.subtitle}`} className="aspect-[4/3]" />
                    </div>
                    <h3 className="font-display font-medium text-lg mt-4 group-hover:text-ember transition-colors duration-300">{p.title}</h3>
                    <p className="text-sm text-graphite/55">{p.subtitle}</p>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {service.faqs?.length > 0 && (
        <section className="bg-cream">
          <div className="max-w-[900px] mx-auto px-5 md:px-10 py-16 md:py-24">
            <Reveal>
              <Overline>[ Frequently Asked Questions ]</Overline>
              {/* Answers rendered fully visible so they are present in the served HTML */}
              <div className="mt-10" data-testid="service-faq-list">
                {service.faqs.map((f, i) => (
                  <div key={i} data-testid={`service-faq-${i}`} className="border-b border-black/10 py-7 first:pt-0 last:border-b-0">
                    <h3 className="font-display font-medium text-base md:text-lg">{f.q}</h3>
                    <p className="text-graphite/60 leading-relaxed text-sm md:text-base mt-2.5">{f.a}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <section className="bg-charcoal text-white">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="font-display font-light text-3xl md:text-4xl tracking-tight">Other services</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mt-10">
            {(allServices || []).filter((s) => s.slug !== slug).slice(0, 4).map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`other-service-${s.slug}`} className="group bg-charcoal hover:bg-graphite p-6 transition-colors duration-300">
                <p className="font-display font-medium group-hover:text-ember transition-colors duration-300">{s.title}</p>
                <span className="inline-flex items-center gap-2 mt-3 text-[10px] font-mono tracking-[0.2em] uppercase text-white/40">View <ArrowUpRight size={12} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream border-t border-black/10">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-14 md:py-20">
          <Reveal>
            <Overline>[ Where We Deliver This ]</Overline>
            <p className="text-graphite/60 text-sm md:text-base leading-relaxed mt-6 max-w-2xl" data-testid="service-where-we-work">
              We deliver {service.title.toLowerCase()} across Bristol, Bath, Somerset, South Wales and Devon — one accountable team, wherever in the region your project sits.
            </p>
            <div className="flex flex-wrap gap-3 mt-8" data-testid="service-area-links">
              {serviceAreas.map((a) => (
                <Link
                  key={a.slug}
                  to={`/areas-we-cover/${a.slug}`}
                  data-testid={`service-area-${a.slug}`}
                  className="group inline-flex items-center gap-2 border border-black/15 px-5 py-3 text-sm text-graphite/70 hover:text-ember hover:border-ember transition-colors duration-300"
                >
                  {a.name}
                  <ArrowUpRight size={13} className="opacity-40 group-hover:opacity-100 transition-opacity duration-300" />
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <RelatedInsights title="Useful Reading" keywords={service.title.split(" ")} />

      <CtaBand title={`Planning ${service.title.toLowerCase().startsWith("a") ? "a" : "a"} project? Let\u2019s talk it through.`} />
    </div>
  );
}
